// frontend/src/pages/SessionLogs.tsx

import React, { useContext, useEffect, useState, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import {
  getSessionLogs,
  createSessionLog,
  updateSessionLog,
  deleteSessionLog,
} from "../api/sessionLogs";
import {
  SessionLog,
  CreateSessionLogRequest,
  UpdateSessionLogRequest,
} from "../types/sessionLog";
import Sidebar from "../components/Sidebar";
import SessionLogForm from "../components/SessionLogForm";
import EditSessionLogForm from "../components/EditSessionLogForm";
import {
  Plus,
  Edit3,
  Trash2,
  Search,
  Download,
  CalendarDays,
  X,
} from "lucide-react";

export default function SessionLogs() {
  const { user } = useContext(AuthContext);

  // — State hooks —
  const [list, setList] = useState<SessionLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editTarget, setEditTarget] = useState<SessionLog | null>(null);

  // — Filters’ state —
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterTeacher, setFilterTeacher] = useState<"all" | string>("all");

  // — Fetch logs once on mount —
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const data = await getSessionLogs();
        if (mounted) setList(data);
      } catch (err) {
        console.error(err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // — Build teacher filter options —
  const teacherOptions = useMemo(
    () => Array.from(new Set(list.map((l) => l.teacherName))).sort(),
    [list],
  );

  // — Apply search + date-range + teacher filters, then sort latest first —
  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase();

    return list
      .filter((log) => {
        const matchesSearch =
          log.topicCovered.toLowerCase().includes(q) ||
          log.notes.toLowerCase().includes(q);

        const logDate = new Date(log.date).toISOString().slice(0, 10);

        const matchesDateFrom =
          !filterDateFrom || logDate >= filterDateFrom;

        const matchesDateTo =
          !filterDateTo || logDate <= filterDateTo;

        const matchesTeacher =
          filterTeacher === "all" || log.teacherName === filterTeacher;

        return (
          matchesSearch &&
          matchesDateFrom &&
          matchesDateTo &&
          matchesTeacher
        );
      })
      .sort(
        (a, b) =>
          new Date(b.date).getTime() - new Date(a.date).getTime(),
      );
  }, [
    list,
    searchTerm,
    filterDateFrom,
    filterDateTo,
    filterTeacher,
  ]);

  // — CSV export of the filtered list —
  const csvEscape = (v: string | number) => {
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };

  const handleExport = () => {
    const headers = [
      "Date",
      "Start",
      "End",
      "Course",
      "Teacher",
      "Location",
      "Topic",
      "Notes",
    ];

    const rows = filtered.map((log) => [
      log.date,
      log.startTime,
      log.endTime,
      log.course.name,
      log.teacherName,
      log.location,
      log.topicCovered,
      log.notes,
    ]);

    const csv = [
      headers.map(csvEscape).join(","),
      ...rows.map((r) => r.map(csvEscape).join(",")),
    ].join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = "session_logs_export.csv";
    a.click();

    URL.revokeObjectURL(url);
  };

  // — CRUD Handlers —
  const handleCreate = async (d: CreateSessionLogRequest) => {
    const newLog = await createSessionLog(d);
    setList((prev) => [newLog, ...prev]);
    setShowNew(false);
  };

  const handleUpdate = async (
    id: string,
    d: UpdateSessionLogRequest,
  ) => {
    const updated = await updateSessionLog(id, d);

    setList((prev) =>
      prev.map((x) => (x.id === id ? updated : x)),
    );

    setEditTarget(null);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this session log?")) return;

    await deleteSessionLog(id);
    setList((prev) => prev.filter((x) => x.id !== id));
  };

  // — Auth guard & perms —
  if (!user) return <Navigate to="/" replace />;

  // Preserved exactly from the existing page.
  const canEdit = user.role == "viewer" || "admin" || "superAdmin";
  const canDelete = user.role === "superadmin";

  const fieldClass = `
    box-border h-10 min-w-0 w-full max-w-full rounded-[9px]
    border border-slate-200 bg-white px-3
    text-[12px] font-medium text-slate-800
    outline-none transition
    placeholder:text-slate-400
    hover:border-slate-300
    focus:border-slate-400
    focus:ring-2 focus:ring-slate-100
  `;

  const actionButtonClass = `
    inline-flex h-8 w-8 shrink-0 items-center justify-center
    rounded-lg border bg-white p-0 transition
    focus:outline-none focus:ring-2
  `;

  const clearFilters = () => {
    setSearchTerm("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterTeacher("all");
  };

  return (
    <div className="min-h-screen bg-[#f7f8fb]">
      <Sidebar />

      <main
        className="
          ml-[280px] min-w-0 px-6 py-6
          transition-[margin] duration-200
          [.sidebar-collapsed_&]:ml-[76px]
          max-lg:ml-[76px]
          max-lg:px-4
          max-sm:px-3
        "
      >
        <div className="mx-auto w-full max-w-[1600px]">
          {/* ============================================================
              HEADER
          ============================================================ */}
          <header className="mb-5 flex items-start justify-between gap-4 max-md:flex-col max-md:items-stretch">
            <div className="flex min-w-0 items-start gap-3">
              <div
                className="
                  grid h-10 w-10 shrink-0 place-items-center
                  rounded-[10px] bg-slate-100 text-slate-600
                "
              >
                <CalendarDays size={20} strokeWidth={1.8} />
              </div>

              <div className="min-w-0">
                <div
                  className="
                    mb-1 text-[10px] font-bold uppercase
                    tracking-[0.12em] text-blue-600
                  "
                >
                  Session management
                </div>

                <h1
                  className="
                    m-0 text-[28px] font-bold
                    tracking-[-0.03em] text-slate-950
                    max-sm:text-[24px]
                  "
                >
                  Session Logs
                </h1>

                <p className="mb-0 mt-1 text-[12px] leading-5 text-slate-500">
                  Track and manage teacher session records.
                </p>
              </div>
            </div>

            {canEdit && (
              <div className="flex shrink-0 items-center gap-2 max-sm:grid max-sm:grid-cols-2">
                <button
                  type="button"
                  onClick={handleExport}
                  className="
                    inline-flex h-10 items-center justify-center gap-2
                    rounded-[9px]
                    border border-emerald-200
                    bg-white px-4
                    text-[11px] font-semibold text-emerald-700
                    transition
                    hover:border-emerald-300
                    hover:bg-emerald-50
                    hover:text-emerald-800
                    focus:outline-none
                    focus:ring-2
                    focus:ring-emerald-200
                  "
                >
                  <Download size={15} />
                  Export
                </button>

                <button
                  type="button"
                  onClick={() => setShowNew(true)}
                  className="
                    inline-flex h-10 items-center justify-center gap-2
                    rounded-[9px]
                    border border-emerald-600
                    bg-emerald-600 px-4
                    text-[11px] font-semibold text-white
                    transition
                    hover:border-emerald-700
                    hover:bg-emerald-700
                    focus:outline-none
                    focus:ring-2
                    focus:ring-emerald-200
                  "
                >
                  <Plus size={15} />
                  New Session
                </button>
              </div>
            )}
          </header>

          {/* ============================================================
              FILTERS
          ============================================================ */}
          {canEdit && (
            <section
              className="
                mb-4 rounded-[16px]
                border border-slate-200 bg-white
                p-4
                shadow-[0_1px_3px_rgba(15,23,42,0.04)]
              "
            >
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(160px,1fr)_minmax(160px,1fr)_minmax(190px,1fr)]">
                {/* Search */}
                <label className="min-w-0">
                  <span
                    className="
                      mb-1.5 block
                      text-[10px] font-semibold uppercase
                      tracking-[0.04em] text-slate-400
                    "
                  >
                    Search session logs
                  </span>

                  <div className="relative">
                    <Search
                      size={16}
                      className="
                        pointer-events-none absolute left-3 top-1/2
                        -translate-y-1/2 text-slate-400
                      "
                    />

                    <input
                      type="text"
                      placeholder="Search by topic or notes"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={`${fieldClass} pl-9`}
                    />
                  </div>
                </label>

                {/* From */}
                <label className="min-w-0">
                  <span
                    className="
                      mb-1.5 block
                      text-[10px] font-semibold uppercase
                      tracking-[0.04em] text-slate-400
                    "
                  >
                    From date
                  </span>

                  <input
                    type="date"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    className={fieldClass}
                  />
                </label>

                {/* To */}
                <label className="min-w-0">
                  <span
                    className="
                      mb-1.5 block
                      text-[10px] font-semibold uppercase
                      tracking-[0.04em] text-slate-400
                    "
                  >
                    To date
                  </span>

                  <input
                    type="date"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    className={fieldClass}
                  />
                </label>

                {/* Teacher */}
                <label className="min-w-0">
                  <span
                    className="
                      mb-1.5 block
                      text-[10px] font-semibold uppercase
                      tracking-[0.04em] text-slate-400
                    "
                  >
                    Teacher
                  </span>

                  <select
                    value={filterTeacher}
                    onChange={(e) => setFilterTeacher(e.target.value)}
                    className={fieldClass}
                  >
                    <option value="all">All Teachers</option>

                    {teacherOptions.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div
                className="
                  mt-4 flex flex-wrap items-center justify-between gap-2
                  border-t border-slate-100 pt-3
                "
              >
                <span className="text-[10px] text-slate-400">
                  Filters apply to session log records.
                </span>

                <button
                  type="button"
                  onClick={clearFilters}
                  className="
                    inline-flex h-8 items-center justify-center gap-1.5
                    rounded-lg
                    border border-emerald-200
                    bg-white px-3
                    text-[13px] font-semibold text-emerald-700
                    transition
                    hover:border-emerald-300
                    hover:bg-emerald-50
                    hover:text-emerald-800
                    focus:outline-none
                    focus:ring-2
                    focus:ring-emerald-200
                  "
                >
                  <X size={13} />
                  Clear filters
                </button>
              </div>
            </section>
          )}

          {/* ============================================================
              LOADING / TABLE
          ============================================================ */}
          {loading ? (
            <section
              className="
                flex min-h-[260px] flex-col items-center justify-center
                rounded-[16px]
                border border-slate-200 bg-white
                shadow-[0_1px_3px_rgba(15,23,42,0.04)]
              "
            >
              <span
                className="
                  h-6 w-6 animate-spin rounded-full
                  border-[3px] border-slate-200 border-t-slate-700
                "
              />

              <span className="mt-3 text-[11px] font-medium text-slate-500">
                Loading session logs…
              </span>
            </section>
          ) : (
            <section
              className="
                overflow-hidden rounded-[16px]
                border border-slate-200 bg-white
                shadow-[0_1px_3px_rgba(15,23,42,0.04)]
              "
            >
              {/* Table card header */}
              <div
                className="
                  flex items-center justify-between gap-4
                  border-b border-slate-100
                  px-5 py-4
                  max-sm:flex-col max-sm:items-start max-sm:px-4
                "
              >
                <div>
                  <h2 className="m-0 text-[18px] font-bold text-slate-950">
                    Session Logs
                  </h2>

                  <p className="mb-0 mt-1 text-[13px] text-slate-500">
                    Showing{" "}
                    <strong className="font-semibold text-slate-600">
                      {filtered.length}
                    </strong>{" "}
                    of{" "}
                    <strong className="font-semibold text-slate-600">
                      {list.length}
                    </strong>{" "}
                    session{list.length === 1 ? "" : "s"}
                  </p>
                </div>

                <div className="rounded-lg bg-slate-50 px-3 py-2 text-[13px] text-slate-500">
                  <strong className="font-semibold text-slate-800">
                    {filtered.length}
                  </strong>{" "}
                  shown
                </div>
              </div>

              {filtered.length > 0 ? (
                <div className="w-full overflow-x-auto">
                  <table className="w-full min-w-[1250px] border-collapse">
                    <thead className="bg-slate-50/80">
                      <tr>
                        {[
                          "Date",
                          "Start",
                          "End",
                          "Course",
                          "Teacher",
                          "Location",
                          "Topic",
                          "Notes",
                        ].map((label) => (
                          <th
                            key={label}
                            className="
                              px-4 py-3.5 text-left
                              text-[14px] font-semibold uppercase
                              tracking-[0.04em] text-slate-600
                            "
                          >
                            {label}
                          </th>
                        ))}

                        {canEdit && (
                          <th
                            className="
                              w-[110px] px-4 py-3.5 text-center
                              text-[14px] font-semibold uppercase
                              tracking-[0.04em] text-slate-600
                            "
                          >
                            Actions
                          </th>
                        )}
                      </tr>
                    </thead>

                    <tbody>
                      {filtered.map((log) => (
                        <tr
                          key={log.id}
                          className="
                            border-b border-slate-100
                            transition-colors
                            last:border-b-0
                            hover:bg-slate-50/60
                          "
                        >
                          <td className="px-4 py-4 align-middle">
                            <div className="min-w-[110px] text-[14px] font-medium text-slate-700">
                              {new Date(log.date).toLocaleDateString()}
                            </div>
                          </td>

                          <td className="px-4 py-4 align-middle">
                            <div className="min-w-[75px] text-[14px] font-medium text-slate-700">
                              {log.startTime}
                            </div>
                          </td>

                          <td className="px-4 py-4 align-middle">
                            <div className="min-w-[75px] text-[14px] font-medium text-slate-700">
                              {log.endTime}
                            </div>
                          </td>

                          <td className="px-4 py-4 align-middle">
                            <div
                              className="
                                min-w-[150px] max-w-[220px]
                                break-words text-[14px]
                                font-semibold text-slate-900
                              "
                            >
                              {log.course.name}
                            </div>
                          </td>

                          <td className="px-4 py-4 align-middle">
                            <div
                              className="
                                min-w-[140px] max-w-[200px]
                                break-words text-[14px]
                                font-medium text-slate-700
                              "
                            >
                              {log.teacherName}
                            </div>
                          </td>

                          <td className="px-4 py-4 align-middle">
                            <div
                              className="
                                min-w-[120px] max-w-[180px]
                                break-words text-[14px] text-slate-600
                              "
                            >
                              {log.location || "—"}
                            </div>
                          </td>

                          <td className="px-4 py-4 align-middle">
                            <div
                              className="
                                min-w-[170px] max-w-[260px]
                                break-words text-[14px]
                                font-medium text-slate-700
                              "
                            >
                              {log.topicCovered || "—"}
                            </div>
                          </td>

                          <td className="px-4 py-4 align-middle">
                            <div
                              className="
                                min-w-[180px] max-w-[300px]
                                break-words text-[13px] leading-5 text-slate-600
                              "
                            >
                              {log.notes || "—"}
                            </div>
                          </td>

                          {canEdit && (
                            <td className="px-4 py-4 text-center align-middle">
                              <div className="inline-flex items-center justify-center gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => setEditTarget(log)}
                                  title="Edit Session"
                                  aria-label="Edit Session"
                                  className={`
                                    ${actionButtonClass}
                                    border-emerald-200
                                    text-emerald-700
                                    hover:border-emerald-300
                                    hover:bg-emerald-50
                                    hover:text-emerald-800
                                    focus:ring-emerald-200
                                  `}
                                >
                                  <Edit3 size={15} />
                                </button>

                                {canDelete && (
                                  <button
                                    type="button"
                                    onClick={() => handleDelete(log.id)}
                                    title="Delete Session"
                                    aria-label="Delete Session"
                                    className={`
                                      ${actionButtonClass}
                                      border-red-200
                                      text-red-600
                                      hover:border-red-300
                                      hover:bg-red-50
                                      hover:text-red-700
                                      focus:ring-red-200
                                    `}
                                  >
                                    <Trash2 size={15} />
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div
                  className="
                    flex min-h-[230px] flex-col
                    items-center justify-center px-6 text-center
                  "
                >
                  <div
                    className="
                      grid h-11 w-11 place-items-center
                      rounded-xl bg-slate-100 text-slate-400
                    "
                  >
                    <CalendarDays size={20} />
                  </div>

                  <h3 className="mb-0 mt-3 text-[13px] font-semibold text-slate-900">
                    No session logs found
                  </h3>

                  <p
                    className="
                      mb-0 mt-1 max-w-[430px]
                      text-[10px] leading-5 text-slate-500
                    "
                  >
                    {searchTerm
                      ? "Try changing your search or filters to see more session logs."
                      : "No session logs are available to display."}
                  </p>
                </div>
              )}
            </section>
          )}

          {/* ============================================================
              MODALS
          ============================================================ */}
          {showNew && (
            <SessionLogForm
              onSubmit={handleCreate}
              onCancel={() => setShowNew(false)}
            />
          )}

          {editTarget && (
            <EditSessionLogForm
              log={editTarget}
              onSubmit={handleUpdate}
              onCancel={() => setEditTarget(null)}
            />
          )}
        </div>
      </main>
    </div>
  );
}
